import { action, runInAction, extendObservable, toJS } from "mobx";

class TeamStore {
    constructor() {
        extendObservable(this, {
            resourceStore: null,
            allPlayers: {},
            allPositions: [],
            teamId: null,
        });
    }

    _updatePlayer(playerId, updater) {
        const player = this.allPlayers[playerId];

        if (!player) return;

        this.allPlayers = {
            ...this.allPlayers,
            [playerId]: {
                ...player,
                ...updater(player),
                isChanged: true,
            },
        };
    }

    _mapByContact(players) {
        const next = {};

        players.forEach((p) => {
            next[p.contact] = p;
        });

        return next;
    }

    _getExcludedIds(isCore) {
        if (!this.resourceStore || isCore === undefined) {
            return [];
        }

        const key = isCore ? "coreSquad" : "squad";
        return toJS(this.resourceStore.data[key]) || [];
    }

    setInitialData = action(
        async (players, positions, teamId, resourceStore) => {
            this.allPlayers = players || {};
            this.allPositions = positions || [];
            this.teamId = teamId;
            this.resourceStore = resourceStore;
        },
    );

    syncToResourceStore(isCore, excludedIds = []) {
        if (!this.resourceStore) return;

        runInAction(() => {
            this.resourceStore.change("allTeamPlayers", this.allPlayers);
            this.resourceStore.change("isSquadChange", true);

            if (isCore !== undefined) {
                this.resourceStore.change(
                    isCore ? "coreSquad" : "squad",
                    excludedIds,
                );
            }
        });
    }

    syncSelection = action((selectedIds, teamId, items, isCore) => {
        let players = Object.values(toJS(this.allPlayers));
        const itemsArray = toJS(items);

        let excludedIds = [...this._getExcludedIds(isCore)];

        const existingTeamPlayers = players.filter(
            (p) => p.team === teamId && p.core === isCore,
        );

        const removedIds = existingTeamPlayers
            .filter((p) => !selectedIds.includes(p.contact))
            .map((p) => p.contact);

        const playersToAdd = [];

        selectedIds.forEach((contact) => {
            const existing = existingTeamPlayers.find(
                (p) => p.contact === contact,
            );

            if (existing && existing.isDeleted) {
                delete existing.isDeleted;
                existing.core = isCore;
                excludedIds.push(contact);
            } else if (!existing) {
                const item = itemsArray.find((i) => i.id === contact);

                if (!item) return;

                playersToAdd.push({
                    ...item,
                    contact,
                    name: `${item.firstName} ${item.lastName}`,
                    passportNumber: item.passportNumber || null,
                    passport: item.passportId || null,
                    team: teamId,
                    notSaved: true,
                    isChanged: true,
                    core: isCore ? true : false,
                    number: null,
                });

                excludedIds.push(contact);
            }
        });

        players.forEach((p) => {
            if (
                p.team === teamId &&
                p.core === isCore &&
                removedIds.includes(p.contact)
            ) {
                p.isDeleted = true;
                p.isChanged = true;
                p.positions = [];
                p.isCaptain = false;
                p.number = 0;

                const idx = excludedIds.indexOf(p.contact);
                if (idx !== -1) excludedIds.splice(idx, 1);
            }
        });

        players = [
            ...players.filter(
                (p) => !playersToAdd.some((a) => a.contact === p.contact),
            ),
            ...playersToAdd,
        ].filter((p) => {
            return !(p.notSaved && p.isDeleted);
        });

        this.allPlayers = this._mapByContact(players);
        this.syncToResourceStore(isCore, excludedIds);
    });

    setCaptain = action((playerId) => {
        const next = { ...this.allPlayers };

        Object.values(next).forEach((p) => {
            if (p.isCaptain && p.contact !== playerId) {
                next[p.contact] = {
                    ...p,
                    isCaptain: false,
                    isChanged: true,
                };
            }
        });

        if (!next[playerId]) return;

        next[playerId] = {
            ...next[playerId],
            isCaptain: !next[playerId].isCaptain,
            isChanged: true,
        };

        this.allPlayers = next;
        this.syncToResourceStore();
    });

    setNumber = action((playerId, number) => {
        const clean = String(number).replace(/\D/g, "").slice(0, 2);

        this._updatePlayer(playerId, () => ({
            number: clean || null,
        }));

        this.syncToResourceStore();
    });

    setPositions = action((playerId, values, allPositions) => {
        const ids = values

            .map((v) => (typeof v === "object" ? v.id : v))
            .filter(Boolean);

        const positions = allPositions.filter((pos) => ids.includes(pos.id));

        this._updatePlayer(playerId, () => ({
            positions,
        }));

        this.syncToResourceStore();
    });

    removePlayer = action((playerId) => {
        const player = this.allPlayers[playerId];
        if (!player) return;

        let excludedIds = [...this._getExcludedIds(player.core)];

        const idx = excludedIds.indexOf(player.contact);
        if (idx !== -1) excludedIds.splice(idx, 1);

        if (player.notSaved) {
            const next = { ...this.allPlayers };
            delete next[playerId];
            this.allPlayers = next;

            this.syncToResourceStore(player.core, excludedIds);
            return;
        }

        this.allPlayers = {
            ...this.allPlayers,
            [playerId]: {
                ...player,
                isDeleted: true,
                isCaptain: false,
                positions: [],
                number: null,
                isChanged: true,
            },
        };

        this.syncToResourceStore(player.core, excludedIds);
    });

    removeNotSavedPlayers = action(() => {
        const filtered = Object.values(this.allPlayers).filter(
            (p) => !p.notSaved,
        );

        this.allPlayers = this._mapByContact(filtered);

        runInAction(() => {
            this.resourceStore.change("allTeamPlayers", this.allPlayers);
        });
    });
}

export const teamStore = new TeamStore();
